import React, { useCallback, memo } from 'react';
import { makeStyles } from 'tss-react/mui';
import EkonomiskAnalysChartQuery, {
  ChartType,
} from '../../Queries/EkonomiskAnalysChartQuery/EkonomiskAnalysChartQuery';
import EDBarChart from '../../../../../../../BaseComponents/Charts/BarChart/EDBarChart';
import EDPieChart from '../../../../../../../BaseComponents/Charts/PieChart/EDPieChart';
import { IFilledLocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  PointOptionsObject,
  AxisLabelsFormatterContextObject,
  Chart,
} from 'highcharts';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import EDLineChart from '../../../../../../../BaseComponents/Charts/LineChart/EDLineChart';

interface IProps {
  id: string | null;
  analys: IFilledLocalAnalys;
  grundurvalId?: string;
  kodbehorighetId: string;
  dates: IEkonomiskAnalysFilledDates;
  grafColumns?: number[];
  detailedSelection: IUIUrval | null;
  chartType: ChartType;
  denomination: number;
  nrDecimals: number;
  afterChartCreated?: (chart: Chart) => void;
}

const useStyles = makeStyles()(theme => ({
  errorWrapper: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(1),
    color: theme.palette.error.main,
    maxWidth: '250px',
  },
}));

const valueLabelText = (denomination: number) => {
  switch (denomination) {
    case 1:
      return 'kr';
    case 1000:
      return 'tkr';
    case 1000000:
      return 'mnkr';
    default:
      return '';
  }
};

const EkonomiskAnalysChart: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const yAxisFormater = useCallback(
    (options: AxisLabelsFormatterContextObject) => {
      const value = (options.value / props.denomination).toString();
      const parts = value.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      if (integerPart.length > 3) {
        const integerPartFormated = integerPart.replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ' '
        );
        return decimalPart !== undefined
          ? [integerPartFormated, decimalPart].join(',')
          : integerPartFormated;
      }

      return decimalPart !== undefined
        ? [integerPart, decimalPart].join(',')
        : integerPart;
    },
    [props.denomination]
  );

  const { classes, cx } = useStyles();

  return (
    <EkonomiskAnalysChartQuery
      type={props.chartType}
      id={props.id}
      analys={props.analys}
      grundurvalId={props.grundurvalId}
      kodbehorighetId={props.kodbehorighetId}
      dates={props.dates}
      detailedSelection={tryConvertUIUrvalToTEDUrval(props.detailedSelection)}
      grafColumns={props.grafColumns}
    >
      {data => {
        if (data.errorMessage) {
          return (
            <div
              className={cx({
                [classes.errorWrapper]: !!data.errorMessage,
              })}
            >
              {data.errorMessage}
            </div>
          );
        }
        return (
          <div>
            {data.warningMessage && (
              <div
                className={cx({
                  [classes.errorWrapper]: !!data.warningMessage,
                })}
              >
                {data.warningMessage}
              </div>
            )}
            {data.title !== '' && (
              <>
                {props.chartType === 'column' && (
                  <EDBarChart
                    title={data.title}
                    yAxisLabel={`Värde - ${valueLabelText(props.denomination)}`}
                    categories={data.categories}
                    series={data.series}
                    formater={yAxisFormater as any} // FIXME: Typning
                    afterChartCreated={props.afterChartCreated}
                  />
                )}
                {props.chartType === 'pie' && (
                  <EDPieChart
                    title={data.title}
                    category={
                      data.series[0].data
                        ? typeof (data.series[0].data[0] as PointOptionsObject)
                            .name === 'string'
                          ? ((data.series[0].data[0] as PointOptionsObject)
                              .name as string)
                          : undefined
                        : ''
                    }
                    data={data.series.map(x => {
                      const point: PointOptionsObject = {
                        name: x.name,
                        y: x.data
                          ? (x.data[0] as PointOptionsObject).y
                            ? ((x.data[0] as PointOptionsObject).y as number) /
                              props.denomination
                            : 0
                          : 0,
                      };

                      return point;
                    })}
                    unitLabel={valueLabelText(props.denomination)}
                    nrDecimals={props.nrDecimals}
                    afterChartCreated={props.afterChartCreated}
                  />
                )}
                {props.chartType === 'line' && (
                  <EDLineChart
                    title={data.title}
                    yAxisLabel={`Värde - ${valueLabelText(props.denomination)}`}
                    categories={data.categories}
                    series={data.series}
                    formater={yAxisFormater as any} // FIXME: Typning
                    afterChartCreated={props.afterChartCreated}
                  />
                )}
              </>
            )}
          </div>
        );
      }}
    </EkonomiskAnalysChartQuery>
  );
};

export default memo(EkonomiskAnalysChart);
